// LIA's task-based operation (context/v1.1: mapa «Ciclo de trabajo por tareas», «Tareas por etapa»; SAIP 04 task
// model). Every active opportunity under LIA keeps ONE open task: action (medio), commercial objective (intención),
// owner and due time; when it closes it records a result from the shared catalog. Browser-safe.
import { z } from 'zod';

/** Commercial objectives shared by every LIA (SAIP 04 «Lista de objetivos comerciales», adapted to the setter role). */
export const SETTER_TASK_OBJECTIVES = [
  'contactar',
  'calificar',
  'avanzar_a_cita',
  'confirmar_cita',
  'reagendar_cita',
  'recibir_visita',
  'atender_transferencia',
  'reactivar',
  'cumplir_compromiso',
  'definir_siguiente_paso',
] as const;
export const SetterTaskObjectiveSchema = z.enum(SETTER_TASK_OBJECTIVES);
export type SetterTaskObjective = z.infer<typeof SetterTaskObjectiveSchema>;

export const SETTER_TASK_OBJECTIVE_LABELS: Record<SetterTaskObjective, string> = {
  contactar: 'Contactar',
  calificar: 'Calificar',
  avanzar_a_cita: 'Avanzar a cita',
  confirmar_cita: 'Confirmar cita',
  reagendar_cita: 'Reagendar cita',
  recibir_visita: 'Recibir la visita',
  atender_transferencia: 'Atender transferencia',
  reactivar: 'Reactivar conversación',
  cumplir_compromiso: 'Cumplir lo acordado',
  definir_siguiente_paso: 'Definir siguiente paso',
};

/** Action = the medium (SAIP 04: «Acción = medio, Objetivo = intención, Salida = resultado»). */
export const SETTER_TASK_ACTIONS = ['whatsapp', 'llamada', 'visita', 'revision'] as const;
export const SetterTaskActionSchema = z.enum(SETTER_TASK_ACTIONS);
export type SetterTaskAction = z.infer<typeof SetterTaskActionSchema>;
export const SETTER_TASK_ACTION_LABELS: Record<SetterTaskAction, string> = { whatsapp: 'Enviar WhatsApp', llamada: 'Llamar', visita: 'Visita', revision: 'Revisar' };

/** Results (SAIP 04 «Lista oficial de salidas», plus the setter-specific transfer and rescheduling outcomes). */
export const SETTER_TASK_RESULTS = ['sin_respuesta', 'respondio', 'pidio_otro_momento', 'avanzo', 'no_fit', 'perdido', 'reactivar_despues', 'transferida', 'reprogramada'] as const;
export const SetterTaskResultSchema = z.enum(SETTER_TASK_RESULTS);
export type SetterTaskResult = z.infer<typeof SetterTaskResultSchema>;
export const SETTER_TASK_RESULT_LABELS: Record<SetterTaskResult, string> = {
  sin_respuesta: 'Ejecutada, sin respuesta todavía',
  respondio: 'Respondió',
  pidio_otro_momento: 'Pidió otro momento',
  avanzo: 'Avanzó de etapa',
  no_fit: 'No fit',
  perdido: 'Perdido',
  reactivar_despues: 'Reactivar después',
  transferida: 'Transferida a una persona',
  reprogramada: 'Reprogramada o sustituida',
};

export const SETTER_TASK_STATUSES = ['open', 'done', 'cancelled'] as const;
export const SetterTaskStatusSchema = z.enum(SETTER_TASK_STATUSES);
export type SetterTaskStatus = z.infer<typeof SetterTaskStatusSchema>;

export const SetterTaskSchema = z.object({
  id: z.string(),
  leadInterestId: z.string(),
  contactId: z.string(),
  contactName: z.string().nullable(),
  projectId: z.string(),
  projectName: z.string().nullable(),
  objective: SetterTaskObjectiveSchema,
  action: SetterTaskActionSchema,
  title: z.string(),
  context: z.string().nullable(),
  dueAt: z.string(),
  ownerKind: z.enum(['agent', 'advisor']),
  ownerName: z.string().nullable(),
  status: SetterTaskStatusSchema,
  result: SetterTaskResultSchema.nullable(),
  resultNote: z.string().nullable(),
  stageAtOpen: z.string(),
  /** No scheduled next action exists: shown as an alert («oportunidad sin siguiente acción»), never mirrored to the CRM. */
  missing: z.boolean(),
  isTest: z.boolean(),
  openedAt: z.string(),
  closedAt: z.string().nullable(),
  /** The CRM task id when mirrored (null = not mirrored yet / not mirrored). */
  crmTaskId: z.string().nullable(),
});
export type SetterTaskDto = z.infer<typeof SetterTaskSchema>;
